import { Button, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import _ from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useGetAllEmployees } from '../../services/react-query/employees';
import { useCreateNote, useGetNoteById, useUpdateNote } from '../../services/react-query/notes';
import { queryClient } from '../../services/react-query/queryClient';
import { queryKeys } from '../../services/react-query/queryKeys';
import { Note, Noteable } from '../../types/Note';
import { Loading } from '../../uikit';
import FileInput from '../../uikit/fileInput/FileInput';
import { JsonifiedFile } from '../../utility/functions';
import { errorPopulator } from '../../utility/inputUtils';
import EmployeeSearchAutocomplete, { EmployeeOption } from '../common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import { useGetNoteEmployeeUuidsById } from '../../services/react-query/notes/useGetNoteEmployeeUuidsById';

interface INoteFormProps {
  uuid: string;
  noteable: Noteable;
  onCancel: () => void;
  setExpandedNoteUuid: (uuid: string | null) => void;
  setNoteSnackbarMessage: Dispatch<SetStateAction<string | null>>;
  parentUuid?: string;
}

const NoteForm: React.FC<INoteFormProps> = ({ uuid, noteable, onCancel, setNoteSnackbarMessage, parentUuid, setExpandedNoteUuid }) => {
  const { noteableUuid, noteableType } = noteable;
  const { data: noteData } = useGetNoteById({ uuid });
  const [note, setNote] = useState<Note | null>(null);
  const [mentions, setMentions] = useState<EmployeeOption[]>([]);
  const [files, setFiles] = useState<JsonifiedFile[]>([]);

  const isReply = parentUuid && uuid === 'new';
  const isNewRoot = !parentUuid && uuid == 'new';
  const { data: noteEmployeeUuidsData } = useGetNoteEmployeeUuidsById({
    uuid: isReply ? parentUuid : uuid,
    action: isReply ? 'reply' : 'edit',
    enabled: !isNewRoot,
  });

  useEffect(() => {
    if (noteData) {
      setNote(noteData);
    }
  }, [noteData]);

  const saveCallback = () => {
    queryClient.invalidateQueries({
      queryKey: [
        queryKeys.allNotes,
        {
          filters: {
            noteable_uuid: noteableUuid,
            noteable_type: noteableType,
          },
        },
      ],
    });
    // to reset state for new note form
    if (uuid === 'new' && noteData) {
      queryClient.invalidateQueries({ queryKey: [queryKeys.allNotes, uuid] });
    }
    setNoteSnackbarMessage('Note Saved Successfully!');
    onCancel();
  };

  const { mutate: createNote } = useCreateNote({
    onSuccessCallback: (newNote: Note) => {
      if (newNote.parent_uuid) {
        setExpandedNoteUuid(newNote.parent_uuid);
      }
      saveCallback();
    },
  });

  const { mutate: updateNote } = useUpdateNote({
    uuid,
    onSuccessCallback: saveCallback,
  });

  const { data: employeeData } = useGetAllEmployees({ params: { per_page: 0 } });

  useEffect(() => {
    if (!(employeeData?.data && noteData)) return;
    setMentions(_.filter(employeeData.data, (employee) => _.includes(noteData.mentions, employee.uuid)));
  }, [uuid, employeeData?.data, noteData]);

  const notificationRecipients: EmployeeOption[] = useMemo(() => {
    return employeeData?.data && noteEmployeeUuidsData?.data
      ? _.filter(employeeData.data, (employee) => _.includes(noteEmployeeUuidsData.data, employee.uuid))
      : [];
  }, [employeeData?.data, noteEmployeeUuidsData?.data]);

  if (!employeeData?.data) return <Loading />;

  const updateNoteData = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote((prev) => (prev ? { ...prev, [event.target.id]: event.target.value } : prev));
  };

  const handleFileChange = (uploadedFiles: JsonifiedFile[]) => {
    setFiles(uploadedFiles);
  };

  const handleSave = () => {
    if (!note) return;

    const payload = {
      noteable_uuid: noteableUuid,
      noteable_type: noteableType,
      text: note.text,
      mentions: _.map(mentions, 'uuid'),
      parent_uuid: note.parent_uuid || parentUuid,
      files,
    };

    if (uuid === 'new') {
      createNote(payload);
    } else {
      updateNote({
        ...payload,
        uuid,
      });
    }
  };

  return (
    <>
      <DialogTitle>{uuid === 'new' ? 'Create new note' : 'Update note'}</DialogTitle>
      <DialogContent sx={{ width: '100%', display: 'grid', gap: 2 }}>
        <TextField
          autoFocus
          id="text"
          label="Note"
          multiline
          rows={5}
          onChange={updateNoteData}
          value={note?.text ?? ''}
          {...errorPopulator(note, 'text')}
          fullWidth
          sx={{ mt: 2 }}
        />

        <EmployeeSearchAutocomplete<true> value={mentions} onChange={(employees) => setMentions(employees)} isMultiple label="Notify" />

        {!_.isEmpty(notificationRecipients) ? (
          <EmployeeSearchAutocomplete<true>
            isDisabled
            value={notificationRecipients}
            onChange={() => null}
            isMultiple
            label="These employees will receive a notification about this message"
          />
        ) : null}

        {/* File Input */}
        {uuid === 'new' && <FileInput onFilesChange={handleFileChange} />}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mx: 2 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          {uuid === 'new' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </>
  );
};

export default NoteForm;
